import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

export interface HLSPlayerError {
  /** 'fatal' triggers the parent's mirror-failover loop. 'recoverable'
   * is informational; the player retries automatically. */
  type: 'fatal' | 'recoverable';
  code: string;
  message: string;
}

interface HLSPlayerProps {
  url: string;
  channelName?: string;
  /** Receives structured errors so the parent can drive mirror failover.
   * Fatal errors should switch to the next source; recoverable ones are
   * already being retried by HLS.js / the <video> element. */
  onError?: (err: HLSPlayerError) => void;
  /** Page-level keys the player can't handle alone (channel up/down,
   * back-to-grid). Caller wires to React Router or a channel switcher. */
  onChannelUp?: () => void;
  onChannelDown?: () => void;
  onBack?: () => void;
}

/**
 * HLS.js video player component.
 *
 * Adapted from the 24-7 news channel's native-hls-player.tsx pattern.
 * Uses HLS.js for cross-browser support; falls back to native <video>
 * for Safari (which has built-in HLS support).
 */
export default function HLSPlayer({ url, channelName, onError, onChannelUp, onChannelDown, onBack }: HLSPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // D-pad / keyboard control. Lives at window level (not on <video>)
  // because the native <video controls> swallows arrow keys for its own
  // seek bar — and TV remotes send through window events anyway. We
  // honor a small set of keys; everything else passes through.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const v = videoRef.current;
      if (!v) return;
      // Skip when typing in any input (no inputs exist in TV mode but
      // be defensive — the player is mounted inside a richer dashboard).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
        case 'MediaPlayPause':  // some remotes send this
          if (v.paused) v.play().catch(() => {});
          else v.pause();
          e.preventDefault();
          break;
        case 'ArrowLeft':
        case 'MediaRewind':
          v.currentTime = Math.max(0, v.currentTime - 10);
          e.preventDefault();
          break;
        case 'ArrowRight':
        case 'MediaFastForward':
          v.currentTime = Math.min(v.duration || Number.POSITIVE_INFINITY, v.currentTime + 10);
          e.preventDefault();
          break;
        case 'ArrowUp':
        case 'MediaTrackPrevious':
          if (onChannelUp) { onChannelUp(); e.preventDefault(); }
          break;
        case 'ArrowDown':
        case 'MediaTrackNext':
          if (onChannelDown) { onChannelDown(); e.preventDefault(); }
          break;
        case 'Escape':
        case 'Backspace':
        case 'GoBack':
          if (onBack) { onBack(); e.preventDefault(); }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onChannelUp, onChannelDown, onBack]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    setLoading(true);
    setError(null);

    // Check if native HLS is supported (Safari)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      video.addEventListener('loadedmetadata', () => setLoading(false));
      video.addEventListener('error', () => {
        const msg = 'Stream playback failed';
        setError(msg);
        onError?.({ type: 'fatal', code: 'native_hls_error', message: msg });
      });
      return;
    }

    // Dynamic import HLS.js for other browsers
    import('hls.js').then(({ default: Hls }) => {
      if (!Hls.isSupported()) {
        setError('HLS not supported in this browser');
        return;
      }

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });

      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_event: unknown, data: any) => {
        if (!data.fatal) return;
        const code = (data.details as string) || (data.type as string) || 'unknown';
        const msg = `Stream error: ${data.type}${data.details ? ` (${data.details})` : ''}`;
        // Network errors might recover from a transient blip — try once,
        // then bubble fatal so the parent switches mirrors.
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setTimeout(() => hls.startLoad(), 1500);
          onError?.({ type: 'fatal', code, message: msg });
          setError(msg);
          return;
        }
        // Media error: try recoverMediaError() once before giving up.
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          try { hls.recoverMediaError(); } catch { /* fall through */ }
        }
        setError(msg);
        onError?.({ type: 'fatal', code, message: msg });
      });
    }).catch(() => {
      setError('Failed to load HLS.js');
    });

    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [url, onError]);

  return (
    <div className="relative w-full bg-black rounded-lg overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading {channelName || 'stream'}...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="text-center text-white">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full aspect-video"
        controls
        playsInline
        autoPlay
      />
    </div>
  );
}
